/* eslint-disable no-nested-ternary */
import React, { FC, HTMLAttributes, MouseEvent, ReactNode, useId, useState } from 'react';
import { styled } from 'linaria/react';
import { defaultTheme, withTheme } from '@shared/lib/theme';
import { useBreakpoint, useOutsideClick } from '@shared/lib/react';
import { BottomSheet, BottomSheetContent } from '@shared/ui/molecules';
import { Text } from '@shared/ui/atoms';
import { ArrowDown } from '@shared/assets/icons';
import Option, { SelectOption } from './Option';
import SelectedValue from './SelectedValue';
export interface SelectProps extends Omit<HTMLAttributes<HTMLElement>, 'onSelect'> {
  color?: keyof typeof defaultTheme.colors.background;
  value: SelectOption | null;
  description?: string;
  error?: string;
  placeholder: string;
  placeholderIcon?: ReactNode;
  options: SelectOption[];
  isClearable?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onSelect?: (value: SelectOption) => void;
  onClear?: (value: string) => void;
}
const Select: FC<SelectProps> = ({
  color = 'secondary',
  value,
  description = '',
  error = '',
  placeholder,
  placeholderIcon,
  options,
  isLoading = false,
  disabled = false,
  onSelect,
  onClear,
  ...rest
}) => {
  const [isSelectionOpen, setIsSelectionOpen] = useState<boolean>(false);
  const id = useId();
  const {
    outsideClickRef
  } = useOutsideClick(() => setIsSelectionOpen(false), id);
  const {
    isMobile
  } = useBreakpoint();
  const handleSelectClick = () => {
    setIsSelectionOpen(prev => !prev);
  };
  const handleSelectionClose = (e: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    setIsSelectionOpen(false);
  };
  const handleOptionSelect = (value: SelectOption) => {
    setIsSelectionOpen(false);
    onSelect?.(value);
  };
  const isError = Boolean(error);
  return <SelectContainer disabled={disabled} isLoading={isLoading}>
      <SelectWrapper type="button" ref={outsideClickRef} isActive={isSelectionOpen} isError={isError} color={color} onClick={handleSelectClick} {...rest}>
        <SelectedValue value={value} placeholder={placeholder} placeholderIcon={placeholderIcon} isLoading={isLoading} />
        <ChevronWrapper isSelectionOpen={isSelectionOpen}>
          <ArrowDown />
        </ChevronWrapper>
        {isSelectionOpen && <>
            {isMobile ? <BottomSheet open={isSelectionOpen} onClose={handleSelectionClose}>
                <BottomSheetContent title={placeholder} onClose={handleSelectionClose}>
                  <OptionsWrapper>
                    {options.map(option => <Option key={option.value} isSelected={value?.value === option.value} option={option} onSelect={handleOptionSelect} />)}
                  </OptionsWrapper>
                </BottomSheetContent>
              </BottomSheet> : <Dropdown color={color}>
                <OptionsWrapper>
                  {options.map(option => <Option key={option.value} isSelected={value?.value === option.value} option={option} onSelect={handleOptionSelect} />)}
                </OptionsWrapper>
              </Dropdown>}
          </>}
      </SelectWrapper>
      {(description || isError) && <BottomTextWrapper>
          {description && !error && <Text family="MTSCompact" size="pS" weight="medium" color="tertiary">
              {description}
            </Text>}
          {error && <Text family="MTSCompact" size="pS" weight="medium" color="negative">
              {error}
            </Text>}
        </BottomTextWrapper>}
    </SelectContainer>;
};
export default Select;
type SelectContainerProps = Required<Pick<SelectProps, 'disabled' | 'isLoading'>>;
const _exp = /*#__PURE__*/() => ({
  disabled
}) => disabled ? '0.5' : '1';
const _exp2 = /*#__PURE__*/() => ({
  disabled,
  isLoading
}) => disabled || isLoading ? 'none' : 'auto';
const SelectContainer = /*#__PURE__*/styled('div')({
  name: "SelectContainer",
  class: "s1mkq5h2",
  propsAsIs: false,
  vars: {
    "s1mkq5h2-0": [_exp()],
    "s1mkq5h2-1": [_exp2()]
  }
});
interface SelectWrapperProps extends Required<Pick<SelectProps, 'color'>> {
  isError: boolean;
  isActive: boolean;
}
const _exp3 = /*#__PURE__*/() => ({
  isActive,
  isError,
  theme
}) => isActive ? theme.colors.stroke.active : isError ? theme.colors.stroke.error : 'transparent';
const _exp4 = /*#__PURE__*/() => ({
  color,
  theme
}) => theme.colors.background[color];
const _exp6 = /*#__PURE__*/() => ({
  isActive,
  theme
}) => isActive ? theme.colors.stroke.active : theme.colors.stroke.hover;
const _exp7 = /*#__PURE__*/() => ({
  isActive,
  theme
}) => isActive ? theme.colors.stroke.active : 'transparent';
const SelectWrapper = withTheme(/*#__PURE__*/styled('button')({
  name: "SelectWrapper",
  class: "s13v99nr",
  propsAsIs: false,
  vars: {
    "s13v99nr-0": [_exp3()],
    "s13v99nr-1": [_exp4()],
    "s13v99nr-2": [_exp6()],
    "s13v99nr-3": [_exp7()]
  }
}));
interface ChevronWrapperProps {
  isSelectionOpen: boolean;
}
const _exp8 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.icon.secondary;
const _exp9 = /*#__PURE__*/() => ({
  isSelectionOpen
}) => isSelectionOpen ? 'rotate(180deg)' : 'rotate(0deg)';
const ChevronWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "ChevronWrapper",
  class: "c1ajsf2b",
  propsAsIs: false,
  vars: {
    "c1ajsf2b-0": [_exp8()],
    "c1ajsf2b-1": [_exp9()]
  }
}));
type DropdownProps = Required<Pick<SelectProps, 'color'>>;
const _exp0 = /*#__PURE__*/() => ({
  color,
  theme
}) => theme.colors.background[color];
const Dropdown = withTheme(/*#__PURE__*/styled('div')({
  name: "Dropdown",
  class: "d1cb710n",
  propsAsIs: false,
  vars: {
    "d1cb710n-0": [_exp0()]
  }
}));
const BottomTextWrapper = /*#__PURE__*/styled('div')({
  name: "BottomTextWrapper",
  class: "b1484gyk",
  propsAsIs: false
});
const OptionsWrapper = /*#__PURE__*/styled('div')({
  name: "OptionsWrapper",
  class: "oc5glln",
  propsAsIs: false
});

require("./Select.linaria.module.css!=!../../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./Select.tsx");